import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Truck, Mail, Phone, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { APEX_LOGO } from "@/lib/brand";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Subscribed. Watch your inbox for freight insights.");
      setEmail("");
    } catch { toast.error("Could not subscribe. Try again."); }
    finally { setLoading(false); }
  };

  return (
    <footer className="bg-[#0B3D6E] text-slate-300" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <Link to="/" className="inline-flex items-center bg-white rounded-lg p-3 shadow-lg ring-1 ring-white/10">
            <img src={APEX_LOGO} alt="Apex Supply Chain Solutions" className="h-16 w-auto object-contain" />
          </Link>
          <p className="mt-6 max-w-md text-sm text-slate-400">
            North American freight and logistics — engineered for reliability, transparency, and long-term partnership.
          </p>

          <form onSubmit={subscribe} className="mt-8 flex gap-2 max-w-md" data-testid="newsletter-form">
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Freight insights, weekly."
              className="flex-1 bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#2E7BC7]"
              data-testid="newsletter-email-input"
            />
            <button disabled={loading} className="btn-primary-apex" data-testid="newsletter-submit-btn">
              {loading ? "..." : "Subscribe"}
            </button>
          </form>
        </div>

        <div>
          <div className="font-label text-xs text-[#2E7BC7] mb-4">Solutions</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/services" className="hover:text-white">White Glove Service</Link></li>
            <li><Link to="/services" className="hover:text-white">Drayage</Link></li>
            <li><Link to="/services" className="hover:text-white">Full Truckload (FTL)</Link></li>
            <li><Link to="/services" className="hover:text-white">Less Than Truckload (LTL)</Link></li>
            <li><Link to="/services" className="hover:text-white">Cross-Border</Link></li>
            <li><Link to="/services" className="hover:text-white">Warehousing</Link></li>
          </ul>
        </div>

        <div>
          <div className="font-label text-xs text-[#2E7BC7] mb-4">Company</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="hover:text-white">About Us</Link></li>
            <li><Link to="/careers" className="hover:text-white">Careers</Link></li>
            <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
            <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
          </ul>
          <div className="mt-6 space-y-2 text-sm text-slate-400">
            <div className="flex items-center gap-2"><Phone className="h-4 w-4" /> 778-861-7890</div>
            <div className="flex items-center gap-2"><Mail className="h-4 w-4" /> supplychain@apexscs.ca</div>
            <div className="flex items-center gap-2"><MapPin className="h-4 w-4" /> Vancouver, BC · Canada</div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-500">© {new Date().getFullYear()} Apex Supply Chain Solutions. All rights reserved.</p>
          <div className="flex items-center gap-6 text-xs text-slate-500">
            <span>SOC 2 Aligned</span>
            <span>C-TPAT Ready</span>
            <span>SmartWay® Partner</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
